import React, { useEffect, useRef, useState } from "react";
import LOCMARKER from "../../../assests/marker-icon.png";
import {
  GoogleMap,
  useLoadScript,
  MarkerF,
  InfoWindowF,
} from "@react-google-maps/api";
import {
  Carousel,
  Drawer,
  Image,
  Row,
  Tag,
  Typography,
} from "../utils/antd-imports/AntdImports";
import "./GoogleMapsComponent.css";
import ProductDetailsCard from "../product-details/mobile/ProductDetailsCard";
import { useMediaQuery } from "react-responsive";
import {
  clearSelectedState,
  setCurrentMapZoom,
  setFiltersApplied,
  setInComeSliderValue,
  setMapCamera,
  setSelectedState,
  setSelectedStateId,
  setShapeOfMap,
  setUserInteracted,
  setZoom,
} from "../../../store/slices/common-slice/product-filters/ProductFilter";
import { useDispatch, useSelector } from "react-redux";
import { getFilterProducts } from "../../../store/thunks/common-thunks/commonThunk";
import { API_BASE_URL_IMAGE } from "../../../services/common";
import { formatToUSCurrency } from "../utils/Utils";
const { Text } = Typography;
const libraries = ["places", "geometry"];
const GEOJSON_URL = "/geojson/world/world-states.json";
const getMapStyle = (isProductModal) => {
  const width = window.innerWidth;
  if (isProductModal && width > 1025) {
    return { width: "100%", height: "50vh" };
  }
  if (isProductModal && width < 1024) {
    return { width: "100%", height: "25vh" };
  }
  if (width >= 1440) return { width: "65vw", height: "66vh" };
  if (width > 769 && width <= 1280) return { width: "58vw", height: "55vh" };
  if (width >= 1024) return { width: "65vw", height: "55vh" };
  if (width <= 768) return { width: "100vw", height: "60vh" };
  return { width: "94vw", height: "57vh" };
};
const GoogleMapsComponent = ({
  location,
  onProductSelect,
  currentPageData,
  showNearby,
  isProductModal,
  productLocations,
  mapPinPointers,
  productsTotalCount,
  mapPinCount,
  isLoading,
  zoomControlState,
  onFilterPayload,
}) => {
  const isStateSelectionRef = useRef(false);
  const stateZoomRef = useRef(null);
  const enableStateHighlightRef = useRef(true);
  const showBoundaryOnlyRef = useRef(false);
  const selectedStateIdRef = useRef(null);
  const activeViewStateRef = useRef(null);
  const activeStateRef = useRef(null);
  const hasUserInteractedRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const selectedDateRangeRef = useRef(null);
  const productTypeRef = useRef(null);
  const priceRangeRef = useRef(null);
  const productSizesRef = useRef();
  const lastZoomRef = useRef(null);
  const lastmapNearbyRef = useRef(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY, // Google map  valid API key
    libraries,
  });
  const isLoggedIn = useSelector((state) => state.login.isAuthenticated);
  const isTab1 = useMediaQuery({ maxWidth: 768 });
  const [drawerHeight, setDrawerHeight] = useState(800); // Start with half-height
  const [productId, setProductId] = useState("");
  const [activeMarker, setActiveMarker] = useState(null);
  const images = activeMarker?.image || [];
  const hasMultipleImages = images.length > 1;
  const STATE_LEVEL_ZOOM = 6;
  const {
    selectedState,
    userInteracted,
    mapCamera,
    selectedStateId,
    filterApplied,
    selectedDateRange,
    productTypes,
    inComeSliderValue,
    selectedSizes,
    filterChecks,
    searchText,
    mapView,
  } = useSelector((state) => state.filters);
  const INITIAL_CENTER = {
    lat: 45,
    lng: -110,
  };
  const [center, setCenter] = useState(INITIAL_CENTER);
  const [map, setMap] = useState(null);
  const [productCoordinates, setProductCoordinates] = useState();
  const [mapContainerStyle, setMapContainerStyle] = useState(
    getMapStyle(isProductModal),
  );
  const markerData = isProductModal ? productCoordinates : mapPinPointers;
  const zoomVal = sessionStorage.getItem("mapZoomVal") ?? 4;
  const [mapZoom, setMapZoom] = useState(() => {
    const stored = Number(sessionStorage.getItem("mapZoomVal"));
    return stored || 4;
  });

  const WORLD_BOUNDS = {
    north: 85,
    south: -85,
    west: -180,
    east: 180,
  };
  const dispatch = useDispatch();
  const mapOptions = {
    gestureHandling: "greedy", // allows full map drag inside the container
    fullscreenControl: false,
  };
  useEffect(() => {
    if (currentPageData) {
      const locations = currentPageData
        ?.map((item) => ({
          lat: parseFloat(item?._id?.lat),
          lng: parseFloat(item?._id?.lng),
          productid: item?._id?.id,
          productTitle: item.product_details?.[0]?.title,
        }))
        ?.filter((item) => !isNaN(item?.lat) && !isNaN(item?.lng));

      setProductCoordinates((prev) => {
        // Only update state if locations are different
        if (JSON.stringify(prev) !== JSON.stringify(locations)) {
          return locations;
        }

        return prev;
      });
    }

    if (showNearby && map) {
      map?.panTo(center);
      map?.setZoom(13);
    } else if (!selectedStateIdRef.current && !hasUserInteractedRef.current) {
      map?.setZoom(4);
    } else if (
      !showNearby &&
      !filterApplied &&
      !userInteracted &&
      map &&
      !hasUserInteractedRef.current
    ) {
      if (!hasUserInteractedRef.current) {
        map?.setZoom(4);
      }
    }
  }, [JSON.stringify(currentPageData), showNearby, map]);
  useEffect(() => {
    selectedDateRangeRef.current = selectedDateRange;
    priceRangeRef.current = inComeSliderValue;
    productSizesRef.current = selectedSizes;
    if (Array.isArray(productTypes) && productTypes.length > 0) {
      productTypeRef.current = productTypes;
    } else if (filterChecks && typeof filterChecks === "object") {
      // extract IDs from filterChecks
      const ids = Object.values(filterChecks)
        ?.flat()
        ?.map((item) => item.id);

      productTypeRef.current = ids || [];
    } else {
      productTypeRef.current = [];
    }
  }, [
    selectedDateRange,
    productTypes,
    inComeSliderValue,
    selectedSizes,
    filterChecks,
  ]);
  // as per the search text making condition
  useEffect(() => {
    if (searchText?.length > 0)
      dispatch(setFiltersApplied(!!searchText?.trim()));
  }, [searchText, dispatch]);
  useEffect(() => {
    if (!map) return;
    // NORMAL MAP MODE
    if (showNearby) {
      if (!lastmapNearbyRef.current) {
        lastmapNearbyRef.current = {
          zoom: map.getZoom(),
          center: map.getCenter().toJSON(),
        };
      }
      stateZoomRef.current = null;
      selectedStateIdRef.current = null;
      enableStateHighlightRef.current = false;
      showBoundaryOnlyRef.current = true;
      selectedStateIdRef.current = null;
      map.data.forEach((feature) => {
        feature.setProperty("hover", false);
        feature.setProperty("selected", false);
      });
      map.panTo(center);
      map.setZoom(13);
    } else {
      enableStateHighlightRef.current = true;
      showBoundaryOnlyRef.current = false;
      hasUserInteractedRef.current = false;
      stateZoomRef.current = null;
      map.setZoom(4);
      if (!filterApplied) {
        map.data.revertStyle();
        map.data.setStyle(map.data.getStyle());
      }
    }
  }, [showNearby, map, center, lastmapNearbyRef]);
  useEffect(() => {
    if (!map) return;
    map.setZoom(mapZoom);
  }, [mapZoom, map]);

  // Runs only when `currentPageData` changes
  useEffect(() => {
    if (productLocations) {
      const lat = productLocations?.lat;
      const lng = productLocations?.lng;
      const newCenter = { lat, lng };
      setCenter(newCenter);
      setProductCoordinates([
        {
          lat,
          lng,
          title: productLocations?.title,
          id: productLocations?.productId,
          price: productLocations?.price,
          image: productLocations?.image,
          city: productLocations?.city,
        },
      ]);

      if (map) {
        map.panTo(newCenter);
        map.setZoom(13);
      }
    }
  }, [productLocations, map]);
  useEffect(() => {
    const handleResize = () => {
      setMapContainerStyle(getMapStyle(isProductModal));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentPageData, isProductModal]);

  // refreshing the map styles after clear the state
  const refreshDataLayerStyle = (map) => {
    map.data.setStyle(map.data.getStyle());
  };

  useEffect(() => {
    if (!map) return;
    if (!selectedState && !selectedStateId && !userInteracted) {
      // Reset refs
      selectedStateIdRef.current = null;
      activeViewStateRef.current = null;
      activeStateRef.current = null;
      stateZoomRef.current = null;
      enableStateHighlightRef.current = true;
      showBoundaryOnlyRef.current = false;
      hasUserInteractedRef.current = false;
      map.data.forEach((feature) => {
        feature.setProperty("selected", false);
        feature.setProperty("hover", false);
      });
      map.data.revertStyle();
      refreshDataLayerStyle(map);
      map.setCenter(INITIAL_CENTER);
      zoomVal ? map.setZoom(zoomVal) : map.setZoom(4);
      setActiveMarker(null);
    }
  }, [selectedState, selectedStateId, map]);

  useEffect(() => {
    if (!map) return;
    if (!filterApplied && !userInteracted) {
      map.setCenter(INITIAL_CENTER);
      map.setZoom(4);
      setCenter(INITIAL_CENTER);
      setMapZoom(4);
      sessionStorage.removeItem("mapZoomVal");
      hasUserInteractedRef.current = false;
      lastZoomRef.current = 4;
    }
  }, [mapView, userInteracted, filterApplied]);

  // Handle Click Event on Product Marker
  const handleProductClick = (product) => {
    setActiveMarker(product);
    if (onProductSelect) {
      onProductSelect(product?.productid); // Notify the parent (`ProductPage`)
      setProductId(product?.productid);
    }
  };
  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  const startDragging = (e) => {
    const startY = e.clientY;
    const startHeight = drawerHeight;

    const onMouseMove = (moveEvent) => {
      const delta = startY - moveEvent.clientY;
      const newHeight = Math.min(
        Math.max(startHeight + delta, 200),
        window.innerHeight - 100,
      );
      setDrawerHeight(newHeight);
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const extendBoundsFromGeometry = (geometry, bounds) => {
    const type = geometry.getType();

    if (type === "Polygon") {
      geometry.getArray().forEach((ring) => {
        ring.getArray().forEach((latlng) => bounds.extend(latlng));
      });
    }

    if (type === "MultiPolygon") {
      geometry.getArray().forEach((polygon) => {
        polygon.getArray().forEach((ring) => {
          ring.getArray().forEach((latlng) => bounds.extend(latlng));
        });
      });
    }
  };
  const clearAllStateFills = (map) => {
    map?.data.forEach((feature) => {
      map?.data.overrideStyle(feature, {
        fillOpacity: 0,
        strokeOpacity: 0,
      });
    });
  };
  const restoreStateFills = (map) => {
    map.data.forEach((feature) => {
      map.data.revertStyle(feature);
    });
  };
  // zoom in action via overlay of map
  const handleZoomIn = () => {
    if (!map) return;
    const currentZoom = map.getZoom();
    map.setZoom(currentZoom + 2);
  };

  // Get the state when the user zooming level
  const detectStateFromCenter = (map) => {
    const center = map.getCenter();
    let detectedState = null;
    map?.data?.forEach((feature) => {
      const geometry = feature?.getGeometry();
      const paths = [];
      geometry?.forEachLatLng((latLng) => {
        paths?.push(latLng);
      });
      if (!paths?.length) return;
      const polygon = new window.google.maps.Polygon({
        paths,
      });
      if (window.google.maps.geometry.poly.containsLocation(center, polygon)) {
        detectedState = feature.getProperty("name");
      }
    });
    const currentZoom = map?.getZoom();
    if (detectedState) {
      if (currentZoom > stateZoomRef?.current)
        dispatch(setSelectedState(detectedState));
    }
  };
  // Helper to get the products by filters
  const handleGetProducts = ({
    feature,
    selectionType,
    map,
    stateId,
    stateName,
    center,
  }) => {
    const zoom = map.getZoom();
    let state_name_map = stateName;
    if (selectionType === "zoom") {
      const mapCenter = map.getCenter();
      state_name_map = [
        {
          lat: mapCenter.lat(),
          lng: mapCenter.lng(),
        },
      ];
    }
    const bookedFrom = selectedDateRangeRef.current?.from || "";
    const bookedTo = selectedDateRangeRef.current?.to || "";
    if (selectionType) {
      dispatch(
        setShapeOfMap(selectionType === "zoom" ? "drag_drop_zoom" : "states"),
      );
      dispatch(setZoom(zoom));
    }
    const payload = {
      page_no: 1,
      page_size: 8,
      product_type: productTypeRef.current ?? [],
      product_fromPrice: priceRangeRef.current?.[0] ?? "",
      product_toPrice: priceRangeRef.current?.[1] ?? "",
      booked_from: bookedFrom,
      booked_to: bookedTo,
      size: productSizesRef.current ?? [],
      search_param: "",
      product_dma: [],
      shape: selectionType === "zoom" ? "drag_drop_zoom" : "states",
      state_name_map,
      ...(selectionType === "zoom" && {
        zoom_in_level: zoom,
      }),
    };
    onFilterPayload?.(payload);
    dispatch(getFilterProducts(payload)).then((res) => {
      if (selectionType === "zoom") {
        // dispatch(setUserInteracted(true)); // keep this line
      }
    });
  };

  // navigating to the product details page
  const handleProductDetails = () => {
    const encodedId = btoa(activeMarker?.id);

    let url = "";
    if (isLoggedIn) {
      url = `/buyer/productsdetails?_id=${encodedId}`;
    } else {
      url = `/productsdetails?_id=${encodedId}`;
    }

    // Open in new tab
    window.open(url, "_blank");
  };

  return (
    <>
      <div className="google-map-container">
        <>
          {!isProductModal && (
            <div className="map-count-overlay">
              {mapPinCount > 0 && (userInteracted || filterApplied) ? (
                <Tag color="rgba(60,60,60,0.9)" className="map-count-tag">
                  {mapPinCount} of {productsTotalCount} products
                </Tag>
              ) : (
                <Tag color="rgba(60,60,60,0.9)" className="map-count-tag">
                  <span onClick={handleZoomIn} className="zoom-in-link">
                    Zoom in
                  </span>{" "}
                  to see products
                </Tag>
              )}
            </div>
          )}
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={zoomVal}
            center={center} // original value
            options={{
              disableDefaultUI: false,
              streetViewControl: false,
              zoomControl: zoomControlState === false ? false : true, // for the zoom in and zoom out feature action
              mapTypeControl: false,
              minZoom: 1.4,
              maxZoom: 20, // prevent zooming too close
              mapOptions,
              cameraControl: false,
              restriction: {
                latLngBounds: WORLD_BOUNDS,
                strictBounds: true,
              },
            }}
            onClick={() => setActiveMarker(null)}
            onLoad={(mapInstance) => {
              //  Save map reference
              setMap(mapInstance);
              if (isProductModal) {
                mapInstance.setOptions({
                  clickableIcons: false,
                });
                return;
              }
              if (showNearby) return;
              // Zoom control position
              mapInstance.setOptions({
                zoomControlOptions: {
                  position: window.google.maps.ControlPosition.TOP_RIGHT,
                },
              });
              // Update count on zoom / pan / idle
              mapInstance.addListener("idle", () => {
                if (showNearby) return;
                // Skip the idle event caused by selecting a state and fitBounds.
                if (isStateSelectionRef.current) {
                  isStateSelectionRef.current = false;
                  return;
                }
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = setTimeout(() => {
                  const zoom = mapInstance.getZoom();
                  if (zoom > STATE_LEVEL_ZOOM && !showNearby) {
                    handleGetProducts({
                      selectionType: "zoom",
                      map: mapInstance,
                      center: {
                        center: mapInstance.getCenter().toJSON(),
                      },
                    });
                  }
                }, 2000);
              });

              // Load GeoJSON AND do everything AFTER it loads
              mapInstance.data.loadGeoJson(GEOJSON_URL, null, (features) => {
                if (showNearby) return;
                if (mapCamera) {
                  mapInstance.setCenter(mapCamera.center);
                  mapInstance.setZoom(mapCamera.zoom);
                }
                if (selectedState) {
                  mapInstance.data.forEach((feature) => {
                    if (feature.getProperty("iso_3166_2") === selectedStateId) {
                      selectedStateIdRef.current = feature;
                      activeViewStateRef.current = feature;
                      feature.setProperty("selected", true);
                      enableStateHighlightRef.current = true;
                      showBoundaryOnlyRef.current = false;
                      const bounds = new window.google.maps.LatLngBounds();
                      extendBoundsFromGeometry(feature.getGeometry(), bounds);
                      mapInstance.fitBounds(bounds);
                      dispatch(setUserInteracted(true));
                      handleGetProducts({
                        feature,
                        selectionType: "restore",
                        map: mapInstance,
                        stateId: selectedStateId,
                        stateName: selectedState,
                      });

                      // Save zoom + camera AFTER fitBounds
                      window.google.maps.event.addListenerOnce(
                        mapInstance,
                        "idle",
                        () => {
                          stateZoomRef.current = mapInstance.getZoom();
                          dispatch(
                            setMapCamera({
                              zoom: mapInstance.getZoom(),
                              center: mapInstance.getCenter().toJSON(),
                            }),
                          );
                        },
                      );
                    }
                  });
                }
                mapInstance.data.setStyle((feature) => {
                  if (showNearby) return;
                  const isHighlightEnabled = enableStateHighlightRef.current;
                  const isHovered =
                    isHighlightEnabled && feature.getProperty("hover");
                  const isSelectedFeature =
                    feature === selectedStateIdRef.current;
                  if (
                    isSelectedFeature ||
                    feature === selectedStateIdRef.current
                  ) {
                    return {
                      clickable: false,
                      cursor: "default",
                      fillOpacity: showBoundaryOnlyRef.current
                        ? 0
                        : isHovered
                          ? 0.3
                          : 0.15,
                      strokeColor: "#1677FF",
                      strokeOpacity: 0.8,
                      strokeWeight: 1.2,
                      zIndex: 10,
                    };
                  }
                  return {
                    clickable: isHighlightEnabled,
                    cursor: isHighlightEnabled ? "pointer" : "default",
                    strokeColor: "#000",
                    strokeOpacity: 0.01,
                    strokeWeight: 1,
                    fillColor: "#449739ff",
                    fillOpacity: isHovered ? 0.4 : 0.01,
                    zIndex: isHovered ? 2 : 1,
                  };
                });

                // HOVER EVENTS (must be after loadGeoJson)
                mapInstance.data.addListener("mouseover", (e) => {
                  if (showNearby) return;
                  if (!enableStateHighlightRef.current) return;
                  e.feature.setProperty("hover", true);
                });

                mapInstance.data.addListener("mouseout", (e) => {
                  if (showNearby) return;
                  if (!enableStateHighlightRef.current) return;
                  e.feature.setProperty("hover", false);
                });

                // CLICK (select state)
                mapInstance.data.addListener("click", (e) => {
                  if (showNearby) return;
                  isStateSelectionRef.current = true;
                  setActiveMarker(null);
                  hasUserInteractedRef.current = true;
                  enableStateHighlightRef.current = true;
                  const stateId = e.feature.getProperty("iso_3166_2");
                  const stateName = e.feature.getProperty("name");
                  activeStateRef.current = stateName;
                  selectedStateIdRef.current = e.feature;
                  activeViewStateRef.current = e.feature;
                  dispatch(setUserInteracted(true));
                  if (hasUserInteractedRef) {
                    dispatch(setUserInteracted(true));
                  }
                  mapInstance.data.forEach((feature) => {
                    feature.setProperty("selected", false);
                  });
                  e.feature.setProperty("selected", true);
                  dispatch(setSelectedState(stateName));
                  dispatch(setSelectedStateId(stateId));

                  handleGetProducts({
                    feature: e.feature,
                    selectionType: "click",
                    map: mapInstance,
                    stateId,
                    stateName,
                  });

                  const bounds = new window.google.maps.LatLngBounds();
                  extendBoundsFromGeometry(e.feature.getGeometry(), bounds);
                  mapInstance.fitBounds(bounds);

                  // Save zoom after fitBounds completes
                  window.google.maps.event.addListenerOnce(
                    mapInstance,
                    "idle",
                    () => {
                      stateZoomRef.current = mapInstance.getZoom();
                      dispatch(
                        setMapCamera({
                          zoom: mapInstance.getZoom(),
                          center: mapInstance.getCenter().toJSON(),
                        }),
                      );
                    },
                  );
                });
                // for the auto reset the highleted state fillColor
                mapInstance.addListener("zoom_changed", () => {
                  if (showNearby) return;
                  localStorage.setItem("STATE_SELECTED", "true");
                  const zoom = mapInstance.getZoom();
                  lastZoomRef.current = zoom;
                  sessionStorage.setItem("mapZoomVal", lastZoomRef.current);

                  if (zoom > STATE_LEVEL_ZOOM) {
                    dispatch(setUserInteracted(true));
                  }
                  setActiveMarker(null);
                  if (!hasUserInteractedRef.current) return;
                  dispatch(
                    setMapCamera({
                      zoom: mapInstance.getZoom(),
                      center: mapInstance.getCenter().toJSON(),
                    }),
                  );
                  if (!selectedStateIdRef.current) return;
                  if (zoom > stateZoomRef?.current) {
                    hasUserInteractedRef.current = true;
                    dispatch(setCurrentMapZoom(zoom));
                    detectStateFromCenter(mapInstance);
                  }
                  if (
                    stateZoomRef?.current !== null &&
                    zoom > stateZoomRef?.current
                  ) {
                    // Disable all state styling
                    enableStateHighlightRef.current = false;
                    showBoundaryOnlyRef.current = true;
                    mapInstance.data.forEach((f) => {
                      f.setProperty("hover", false);
                    });
                    // force style refresh
                    clearAllStateFills(mapInstance);
                    mapInstance.data.revertStyle();
                    dispatch(setUserInteracted(true));
                    return;
                  }
                  if (
                    stateZoomRef.current !== null &&
                    zoom <= stateZoomRef.current &&
                    selectedStateIdRef.current
                  ) {
                    enableStateHighlightRef.current = false;
                    showBoundaryOnlyRef.current = true;
                    hasUserInteractedRef.current = false;
                    if (!selectedState) {
                      clearAllStateFills(mapInstance);
                      mapInstance.data.revertStyle();
                    }
                  }

                  //re-enable when zooming back out
                  if (
                    stateZoomRef.current !== null &&
                    zoom <= stateZoomRef.current
                  ) {
                    enableStateHighlightRef.current = true;
                    showBoundaryOnlyRef.current = false;
                    if (!selectedState) {
                      dispatch(clearSelectedState());
                      clearAllStateFills(mapInstance);
                      mapInstance.data.revertStyle();
                      restoreStateFills(mapInstance);
                    }
                  }
                });
                // getting the state from drag
                mapInstance.addListener("dragstart", () => {
                  setActiveMarker(null);
                  hasUserInteractedRef.current = true;
                });
              });
            }}
          >
            {/* As of now showing the markers when the selected state or zoomed state level*/}

            {!isLoading &&
              (userInteracted || filterApplied || isProductModal === true) &&
              markerData?.map((product, i) => {
                const iconUrl = product?.product_type_icon?.[0]
                  ? `${API_BASE_URL_IMAGE}${product.product_type_icon[0]}`
                  : LOCMARKER;
                return (
                  <MarkerF
                    title={product?.title}
                    onClick={() => handleProductClick(product)}
                    position={product}
                    icon={{ url: iconUrl }}
                  />
                );
              })}
            {activeMarker && (
              <InfoWindowF
                position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
                options={{
                  closeBoxURL: "", //  removes X button
                  disableAutoPan: true,
                }}
              >
                <div
                  style={{ width: isTab1 ? 180 : 260, cursor: "pointer" }}
                  onClick={(e) => {
                    if (
                      e.target.closest(".slick-prev") ||
                      e.target.closest(".slick-next") ||
                      e.target.closest(".slick-dots")
                    ) {
                      e.stopPropagation();
                      return;
                    }
                    e.stopPropagation();
                    handleProductDetails();
                  }}
                >
                  <Carousel
                    arrows={hasMultipleImages}
                    dots={hasMultipleImages}
                    infinite={hasMultipleImages}
                    autoplay={hasMultipleImages}
                    autoplaySpeed={5000}
                  >
                    {activeMarker?.image?.map((img, index) => (
                      <div key={index}>
                        <Image
                          src={`${API_BASE_URL_IMAGE}${img}`}
                          width="100%"
                          style={{ objectFit: "cover", borderRadius: 8 }}
                          preview={false}
                        />
                      </div>
                    ))}
                  </Carousel>
                  <div style={{ padding: "20px" }}>
                    <Row style={{ display: "flex", flexDirection: "column" }}>
                      <Text strong className="price-text">
                        {formatToUSCurrency(activeMarker?.price)}
                      </Text>
                      <Text
                        strong
                        className="product-title-mobile-view truncate"
                      >
                        {" "}
                        {activeMarker.title}
                      </Text>
                    </Row>
                    <Row>
                      <Text style={{ fontFamily: "Roboto" }}>
                        {activeMarker.city}
                      </Text>
                    </Row>
                  </div>
                </div>
              </InfoWindowF>
            )}
          </GoogleMap>
        </>
      </div>
    </>
  );
};

export default GoogleMapsComponent;
